import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { company } from "../data/company";
import EmailAddress from "./EmailAddress";
import services from "../data/services";
import "./Contact.css";

const emptyForm = {
  name: "",
  phone: "",
  email: "",
  place: "",
  service: services[0].title,
  message: "",
};

const Contact = () => {
  const [mapOpen, setMapOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState("idle");

  const contactStructuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    mainEntity: {
      "@type": "LocalBusiness",
      name: company.legalName,
      address: {
        "@type": "PostalAddress",
        streetAddress: company.street,
        addressLocality: company.city,
        postalCode: company.postalCode,
        addressRegion: "Berlin",
        addressCountry: "DE",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: company.lat,
        longitude: company.lng,
      },
      telephone: company.phones.map((phone) => phone.tel),
      email: company.email,
      openingHours: "Mo-Fr 06:00-18:00",
    },
  };

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const submitOffer = (event) => {
    event.preventDefault();
    const body = [
      `Name: ${form.name}`,
      `Telefon: ${form.phone}`,
      `E-Mail: ${form.email}`,
      `Ort: ${form.place}`,
      `Leistung: ${form.service}`,
      "",
      form.message || "–",
    ].join("\n");
    const subject = "Angebotsanfrage über mfd-berlin.com";
    window.location.href = `mailto:${company.inquiryEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setStatus("success");
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(contactStructuredData)}
        </script>
      </Helmet>
      <section className="contact-section" id="kontakt">
        <h2>Kontakt</h2>
        <h3>Wir freuen uns auf Ihre Anfrage!</h3>
        <div className="contact-wrapper">
          <div className="contact-info">
            <div className="address-item">
              <div className="address-icon">📍</div>
              <div className="address-content">
                <div className="address-line">{company.street}</div>
                <div className="address-line">
                  {company.postalCode} {company.city}
                </div>
                <div className="address-line">Deutschland</div>
                <a
                  className="map-link"
                  href="https://maps.app.goo.gl/cWKyX7XY351iXZV66"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Auf Karte anzeigen →
                </a>
              </div>
            </div>

            {company.phones.map((phone) => (
              <div className="contact-line" key={phone.tel}>
                <span className="contact-icon" aria-hidden="true">📞</span>
                <a href={`tel:${phone.tel}`}>{phone.display}</a>
              </div>
            ))}

            <div className="contact-line contact-email">
              <span className="contact-icon" aria-hidden="true">✉️</span>
              <a href={`mailto:${company.email}`}>
                <EmailAddress email={company.email} />
              </a>
            </div>

            <div className="contact-line">
              <span className="contact-icon" aria-hidden="true">🕐</span>
              <span className="contact-hours">
                {company.hoursLabel.split(", ").map((part, index, parts) => (
                  <span key={part}>
                    {index < parts.length - 1 ? `${part},` : part}
                  </span>
                ))}
              </span>
            </div>
          </div>

          <div className="map-container">
            {mapOpen ? (
              <iframe
                title="Standort M.F.D. Dienstleistungs GmbH & Co. KG, Großbeerenstraße 2-10, Berlin"
                src={`https://maps.google.com/maps?q=${company.lat},${company.lng}&z=16&hl=de&output=embed`}
                width="600"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            ) : (
              <button
                type="button"
                className="map-placeholder"
                onClick={() => setMapOpen(true)}
              >
                Karte laden
                <span>Google Maps wird erst nach diesem Klick geladen.</span>
              </button>
            )}
          </div>
        </div>

        <form className="offer-form" onSubmit={submitOffer}>
          <h3>Angebot anfordern</h3>
          <div className="offer-grid">
            <label>
              Name
              <input name="name" value={form.name} onChange={updateField} required autoComplete="name" />
            </label>
            <label>
              Telefon
              <input name="phone" value={form.phone} onChange={updateField} required autoComplete="tel" />
            </label>
            <label>
              E-Mail
              <input name="email" type="email" value={form.email} onChange={updateField} required autoComplete="email" />
            </label>
            <label>
              Ort in Berlin oder Umgebung
              <input name="place" value={form.place} onChange={updateField} required />
            </label>
            <label className="offer-span">
              Leistung
              <select name="service" value={form.service} onChange={updateField}>
                {services.map((service) => (
                  <option key={service.slug} value={service.title}>
                    {service.title}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label>
            Nachricht
            <textarea name="message" rows="5" value={form.message} onChange={updateField} />
          </label>
          <button type="submit" className="offer-submit" disabled={status === "sending"}>
            {status === "sending" ? "Wird gesendet…" : "Anfrage senden"}
          </button>
          {status === "success" && (
            <p className="offer-hint offer-success">
              Ihr E-Mail-Programm ist geöffnet. Bitte senden Sie die Nachricht ab. Sie geht an{" "}
              <EmailAddress email={company.inquiryEmail} />.
            </p>
          )}
          {status === "error" && (
            <p className="offer-hint offer-error">
              Die Anfrage konnte nicht gesendet werden. Bitte schreiben Sie direkt an{" "}
              <a href={`mailto:${company.inquiryEmail}`}><EmailAddress email={company.inquiryEmail} /></a>.
            </p>
          )}
        </form>
      </section>
    </>
  );
};

export default Contact;
